import { Button, Input, Spacer, VDBText } from "@components/atoms";
import { ButtonCategory, ButtonType } from "@components/atoms/Button/types";
import { SpaceType } from "@components/atoms/Spacer/types";
import { Controller, useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import React, { useState } from "react";
import styled, { useTheme } from "styled-components/native";
import { useTypographyStore } from "@redux/slices/typographySlice";
import { useSaveSavedSearch } from "@apis/searchResult";
import { SavedSearchPopupProps } from "./types";
import { productNameToTokenCategory } from "@utils/index";
import { showToast, ToastTypes } from "@components/molecules/VDBToast/util";
import { useTranslation } from "react-i18next";
import { ProductCategory, ProductTypeCommon } from "@utils/constants";
import WebPopup from "@components/atoms/PopUpWeb";
import { Platform } from "react-native";
import { Divider } from "@gluestack-ui/themed";
import { useQueryClient } from "@tanstack/react-query";
import { useSearchFiltersSlice } from "@redux/slices/searchFilters";
import { filterSearchFilters } from "@scenes/explore/utils";
import { useUserInfo } from "@redux/slices/userInfoSlice";
import {
  generateGemstoneSavedSearchName,
  generateJewelrySavedSearchName,
  generateSavedSearchName,
} from "@utils/product/productFormatters";

const SavedSearchPopup = ({
  onSavedSearchPopupShowChange,
  queryParamList,
  productType,
}: SavedSearchPopupProps) => {
  const typography = useTypographyStore();
  const theme = useTheme();
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const { filters, collection, sub_collection } = useSearchFiltersSlice();
  const { getPriceMode } = useUserInfo();
  let jewelryFilters = filterSearchFilters(
    filters?.[ProductCategory.JEWELRY],
    getPriceMode()
  );
  const tokenCategory = productNameToTokenCategory(
    productType
  ) as ButtonCategory;
  const [isEnabledSavedButton, setIsEnabledSavedButton] = useState(false);

  const savedSearchSchema = () => {
    return yup
      .object({
        title: yup.string(),
      })
      .required();
  };

  const { control, handleSubmit } = useForm({
    resolver: yupResolver(savedSearchSchema()),
  });
  const createSavedSearch = useSaveSavedSearch();

  const getTypeFromProduct = (pType: string) => {
    switch (pType) {
      case ProductCategory.DIAMOND:
        return 1;
      case ProductCategory.GEMSTONE:
        return 2;
      case ProductCategory.JEWELRY:
        return 3;
      case ProductCategory.LAB_GROWN_DIAMOND:
        return 4;
      default:
        return 1;
    }
  };

  const handleCreateSaveSearch = (data: any) => {
    let savedSearch = {};
    switch (productType) {
      case ProductCategory.DIAMOND:
      case ProductCategory.LAB_GROWN_DIAMOND:
        savedSearch = generateSavedSearchName(queryParamList, t);
        break;
      case ProductCategory.GEMSTONE:
        savedSearch = generateGemstoneSavedSearchName(queryParamList, t);
        break;
      case ProductCategory.JEWELRY:
        const subType =
          productType === ProductTypeCommon.LABGROWN_JEWELRY_SUB_TYPE
            ? "Lab-Grown Jewelry"
            : "Jewelry";
        const _jewelryFilters =
          jewelryFilters.find((filter) => filter?.name === subType)?.filters ??
          [];
        savedSearch = generateJewelrySavedSearchName(
          queryParamList,
          t,
          _jewelryFilters,
          collection ?? [],
          sub_collection ?? []
        );
        break;
      default:
        savedSearch = generateSavedSearchName(queryParamList, t);
        break;
    }

    const payload = {
      saved_search: {
        search_options: JSON.stringify(savedSearch.options),
        type: getTypeFromProduct(productType),
        title: data.title,
      },
    };
    createSavedSearch.mutate(payload, {
      onSuccess(successData: any) {
        if (successData.data?.saved_search) {
          onSavedSearchPopupShowChange(false);
          showToast(
            t(successData.data?.translation_key ?? successData.data?.message),
            `${t(data.title)}`,
            ToastTypes.SUCCESS,
            true,
            true
          );
          queryClient.invalidateQueries({ queryKey: ["SAVED_SEARCH"] }).then();
        }
      },
    });
  };

  return (
    <WebPopup
      isOpen={true}
      title={t("searchResult.createSavedSearch.popup.title.text")}
      onClose={() => onSavedSearchPopupShowChange(false)}
    >
      <HeaderContainer>
        <VDBText type={typography["H2-R"]} color={theme["core-06"]}>
          {t("searchResult.createSavedSearch.popup.subTitle.text")}
        </VDBText>
      </HeaderContainer>
      <BodyContainer>
        <Controller
          control={control}
          render={({
            field: { onChange, onBlur, value },
            formState: { errors },
          }) => (
            <Input
              keyboardType={
                Platform.OS === "ios" ? "ascii-capable" : "visible-password"
              }
              inputLabel={t(
                "searchResult.createSavedSearch.popup.input.placeholder.text"
              )}
              placeholder={t(
                "searchResult.createSavedSearch.popup.input.placeholder.text"
              )}
              onBlur={onBlur}
              onChangeText={(text) => {
                // Regex to match a wide range of emojis
                const emojiRegex =
                  /[\u{1F600}-\u{1F64F}]|[\u{1F300}-\u{1F5FF}]|[\u{1F680}-\u{1F6FF}]|[\u{1F700}-\u{1F77F}]|[\u{1F780}-\u{1F7FF}]|[\u{1F800}-\u{1F8FF}]|[\u{1F900}-\u{1F9FF}]|[\u{1FA00}-\u{1FA6F}]|[\u{1FA70}-\u{1FAFF}]|[\u{2600}-\u{26FF}]|[\u{2700}-\u{27BF}]|[\u{1F910}-\u{1F918}]/gu;
                const filteredText = text.replace(emojiRegex, "");
                onChange(filteredText);
                setIsEnabledSavedButton(!!filteredText.trim());
              }}
              value={value}
              error={errors?.title?.message}
            />
          )}
          name="title"
        />
        <Spacer size={SpaceType.Big} />
        <Divider />
        <Spacer size={SpaceType.Big} />
        <Button
          category={tokenCategory}
          type={ButtonType.primary}
          title={t("searchResult.createSavedSearch.popup.primaryButton.text")}
          loading={!!createSavedSearch.isLoading}
          disabled={!isEnabledSavedButton || !!createSavedSearch.isLoading}
          onPress={handleSubmit(handleCreateSaveSearch)}
        />
        <Spacer size={SpaceType.Double} />
        <Button
          category={tokenCategory}
          type={ButtonType.secondary}
          title={t("searchResult.createSavedSearch.popup.secondaryButton.text")}
          onPress={() => onSavedSearchPopupShowChange(false)}
        />
      </BodyContainer>
    </WebPopup>
  );
};

const HeaderContainer = styled.View`
  padding-right: ${SpaceType.Double}px;
  padding-right: ${SpaceType.Double}px;
`;

const BodyContainer = styled.View`
  padding-top: ${SpaceType.Big}px;
`;

export default SavedSearchPopup;
